/**
 * Tableau de bord.
 *
 * `09-rapports-exports-kpi.md` distingue deux niveaux de lecture : les indicateurs « du jour »,
 * calculés à la volée, et les indicateurs « de tendance », qui viennent du dernier instantané
 * quotidien. `meta.instantane_du` vaut `null` tant qu'aucun instantané n'a encore été calculé — ce
 * n'est pas une erreur, c'est un état normal en tout début de vie du système, et l'écran le dit
 * plutôt que d'afficher une section de tendance vide sans explication.
 */
import { Link } from "react-router-dom";

import { useIdentite } from "../../shared/auth/contexte";
import type { Indicateur } from "../../shared/api/types";
import { Chargement, Erreur, ValeurOuRaison } from "../../shared/composants/Etats";
import { formaterDateSeule } from "../../shared/composants/formatage";
import { InfoBulle } from "../../shared/composants/InfoBulle";
import { useTableauDeBord } from "./requetes";

const LIBELLE_CATEGORIE: Record<string, string> = {
  disponibilite: "Disponibilité",
  trackers: "Trackers",
  logistique: "Logistique",
  maintenance: "Maintenance",
  conformite: "Conformité",
  facturation: "Facturation",
  anomalies: "Anomalies",
};

/** Regroupe des indicateurs par catégorie, dans l'ordre stable de `LIBELLE_CATEGORIE`. */
function parCategorie(indicateurs: Indicateur[], visibles: [string, string][]) {
  return visibles
    .map(([code, libelle]) => ({
      code,
      libelle,
      indicateurs: indicateurs.filter((i) => i.categorie === code),
    }))
    .filter((groupe) => groupe.indicateurs.length > 0);
}

export function EcranTableauDeBord() {
  const identite = useIdentite();
  const requete = useTableauDeBord();

  if (requete.isPending) return <Chargement libelle="Calcul des indicateurs…" />;
  if (requete.isError) {
    return <Erreur erreur={requete.error} reessayer={() => void requete.refetch()} />;
  }

  const tableau = requete.data.data;
  const indicateurs = tableau.indicateurs ?? [];
  const parJour = indicateurs.filter((i) => i.niveau === "jour");
  const parTendance = indicateurs.filter((i) => i.niveau === "tendance");

  // La facturation est réservée aux profils TotalEnergies et Support (CDC §10.7) : un lien vers un
  // écran qu'Air Tahiti ne peut pas ouvrir serait trompeur.
  const categoriesVisibles = Object.entries(LIBELLE_CATEGORIE).filter(
    ([code]) => code !== "facturation" || identite.profil !== "air-tahiti",
  );

  const groupesJour = parCategorie(parJour, categoriesVisibles);
  const groupesTendance = parCategorie(parTendance, categoriesVisibles);

  return (
    <section>
      <h1>Tableau de bord</h1>
      <p className="tableau-de-bord__parc">
        Parc suivi : <strong>{tableau.parc_total}</strong> GRV
      </p>

      <h2>Aujourd'hui</h2>
      <div className="grille-cartes">
        {groupesJour.map((groupe) => (
          <Link
            key={groupe.code}
            to={`/kpi/${groupe.code}`}
            className={`carte carte--lien categorie categorie--${groupe.code}`}
            aria-label={groupe.libelle}
          >
            <h3>{groupe.libelle}</h3>
            <dl>
              {groupe.indicateurs.map((indicateur) => (
                <div className="carte__champ" key={indicateur.code}>
                  <dt>
                    {indicateur.libelle}
                    <InfoBulle texte={indicateur.description} libelle={indicateur.libelle} />
                  </dt>
                  <dd>
                    <ValeurOuRaison
                      valeur={indicateur.valeur}
                      unite={indicateur.unite}
                      indisponibleCar={indicateur.indisponible_car}
                    />
                  </dd>
                </div>
              ))}
            </dl>
          </Link>
        ))}
      </div>

      <h2>Tendance</h2>
      {tableau.instantane_du ? (
        <>
          <p className="tableau-de-bord__instantane">
            Calculée au {formaterDateSeule(tableau.instantane_du)}
          </p>
          <div className="grille-cartes">
            {groupesTendance.map((groupe) => (
              <Link
                key={groupe.code}
                to={`/kpi/${groupe.code}`}
                className={`carte carte--lien categorie categorie--${groupe.code}`}
                aria-label={groupe.libelle}
              >
                <h3>{groupe.libelle}</h3>
                <dl>
                  {groupe.indicateurs.map((indicateur) => (
                    <div className="carte__champ" key={indicateur.code}>
                      <dt>
                        {indicateur.libelle}
                        <InfoBulle texte={indicateur.description} libelle={indicateur.libelle} />
                      </dt>
                      <dd>
                        <ValeurOuRaison
                          valeur={indicateur.valeur}
                          unite={indicateur.unite}
                          indisponibleCar={indicateur.indisponible_car}
                        />
                      </dd>
                    </div>
                  ))}
                </dl>
              </Link>
            ))}
          </div>
        </>
      ) : (
        <p className="etat etat--vide">
          Aucun instantané quotidien calculé pour l'instant. Les indicateurs de tendance
          apparaîtront après le premier calcul planifié.
        </p>
      )}
    </section>
  );
}
